/**
 * Nolana theme — add to cart and quantity controls.
 * Replaces the demo's localStorage cart with the Shopify Cart AJAX API.
 *
 * Markup contract:
 *   [data-add-to-cart][data-variant-id]        one-click add (product cards, hero, promo)
 *       optional [data-quantity-from="#id"]    quantity input to read
 *       optional [data-properties='{"k":"v"}'] line item properties (JSON)
 *   <form data-product-form>                   product page form (FormData is posted as-is)
 *   [data-qty-dec] / [data-qty-inc] next to [data-qty-input]   quantity steppers
 *
 * Flow: POST /cart/add.js with `sections=cart-drawer,cart-icon-bubble` → swap the header
 * counter → emit "nolana:cart:updated" {cart sections, source} → open the cart drawer.
 * The cart drawer module (assets/nolana-cart-drawer.js) listens for the event.
 */
(function () {
  "use strict";

  var SECTIONS = ["cart-drawer", "cart-icon-bubble"];

  function nolana() {
    return window.Nolana || {};
  }

  function strings() {
    return (window.NolanaConfig && window.NolanaConfig.strings) || {};
  }

  function routes() {
    return window.routes || {};
  }

  function sectionInnerHTML(html) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    var section = doc.querySelector(".shopify-section") || doc.body;
    return section.innerHTML;
  }

  function updateCartBubble(sections) {
    var bubble = document.getElementById("cart-icon-bubble");
    if (bubble && sections && sections["cart-icon-bubble"]) {
      bubble.innerHTML = sectionInnerHTML(sections["cart-icon-bubble"]);
    }
  }

  function setBusy(element, busy) {
    if (!element) return;
    element.classList.toggle("is-loading", busy);
    element.setAttribute("aria-busy", busy ? "true" : "false");
    if (element.tagName === "BUTTON") element.disabled = busy;
  }

  function fetchJson(url, options) {
    return fetch(url, options).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || strings().cartError || "Cart error");
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  function afterAdd(data, source) {
    updateCartBubble(data.sections);
    document.dispatchEvent(
      new CustomEvent("nolana:cart:updated", {
        detail: { sections: data.sections, source: source, added: data.items || [data] },
      })
    );
    var layers = nolana().layers;
    if (layers && layers.open("CartDrawer", source)) return;
    if (typeof nolana().toast === "function") nolana().toast(strings().addedToCart || "");
  }

  function reportError(error) {
    // Only Cart API (422) descriptions are user-facing; network and parse errors get the generic text.
    var message = error && error.data ? error.message : strings().cartError || "";
    if (typeof nolana().toast === "function" && message) nolana().toast(message);
    document.dispatchEvent(new CustomEvent("nolana:cart:error", { detail: { error: error } }));
  }

  function addItems(items, source) {
    setBusy(source, true);
    return fetchJson(routes().cart_add_url || "/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: items,
        sections: SECTIONS.join(","),
        sections_url: window.location.pathname,
      }),
    })
      .then(function (data) {
        afterAdd(data, source);
        return data;
      })
      .catch(reportError)
      .finally(function () {
        setBusy(source, false);
      });
  }

  function quantityFor(trigger) {
    var selector = trigger.getAttribute("data-quantity-from");
    if (!selector) return 1;
    var input = document.querySelector(selector);
    if (!input) return 1;
    var value = parseInt(input.value || input.textContent, 10);
    return Math.max(1, isNaN(value) ? 1 : value);
  }

  function propertiesFor(trigger) {
    var raw = trigger.getAttribute("data-properties");
    if (!raw) return undefined;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return undefined;
    }
  }

  /* ---------- One-click add buttons ---------- */
  document.addEventListener("click", function (event) {
    var trigger = event.target.closest("[data-add-to-cart]");
    if (!trigger || trigger.disabled) return;
    var variantId = parseInt(trigger.getAttribute("data-variant-id"), 10);
    if (!variantId) return;
    event.preventDefault();
    var item = { id: variantId, quantity: quantityFor(trigger) };
    var properties = propertiesFor(trigger);
    if (properties) item.properties = properties;
    addItems([item], trigger);
  });

  /* ---------- Product page form ---------- */
  document.addEventListener("submit", function (event) {
    var form = event.target.closest("form[data-product-form]");
    if (!form) return;
    event.preventDefault();
    var submit = form.querySelector('[type="submit"]');
    var formData = new FormData(form);
    formData.append("sections", SECTIONS.join(","));
    formData.append("sections_url", window.location.pathname);
    setBusy(submit, true);
    fetchJson(routes().cart_add_url || "/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: formData,
    })
      .then(function (data) {
        afterAdd(data, submit);
      })
      .catch(reportError)
      .finally(function () {
        setBusy(submit, false);
      });
  });

  /* ---------- Quantity steppers ---------- */
  document.addEventListener("click", function (event) {
    var button = event.target.closest("[data-qty-dec], [data-qty-inc]");
    if (!button) return;
    var wrapper = button.closest("[data-qty]") || button.parentElement;
    var input = wrapper.querySelector("[data-qty-input]");
    if (!input) return;
    event.preventDefault();
    var min = input.min === "" ? 1 : parseInt(input.min, 10);
    if (isNaN(min)) min = 1;
    var max = input.max === "" ? Infinity : parseInt(input.max, 10);
    if (isNaN(max)) max = Infinity;
    var step = parseInt(input.step, 10) || 1;
    var previous = parseInt(input.value, 10);
    if (isNaN(previous)) previous = min;
    var value = previous + (button.hasAttribute("data-qty-inc") ? step : -step);
    value = Math.min(max, Math.max(min, value));
    if (value === previous) return;
    input.value = value;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  window.Nolana = window.Nolana || {};
  window.Nolana.cart = window.Nolana.cart || {};
  window.Nolana.cart.add = addItems;
  window.Nolana.cart.updateBubble = updateCartBubble;
  window.Nolana.cart.sectionInnerHTML = sectionInnerHTML;
})();
